/**
 * 技能选择器组件
 *
 * 用于选择启用智能体的技能，支持：
 * - 显示所有可用技能列表
 * - 复选框切换启用状态
 * - 系统技能标识
 * - 点击预览技能内容
 */

import React, { useState, useEffect, useCallback } from 'react';
import { skillApi } from '../../services/skillApi';
import type { Skill } from '../../types/skill';

interface SkillSelectorProps {
  /** 已启用的技能名称列表 */
  enabledSkills: string[];
  /** 技能变更回调 */
  onChange: (skills: string[]) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 点击技能预览回调 */
  onPreviewSkill?: (skillName: string) => void;
}

export const SkillSelector: React.FC<SkillSelectorProps> = ({
  enabledSkills,
  onChange,
  disabled = false,
  onPreviewSkill,
}) => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 加载技能列表
  useEffect(() => {
    const fetchSkills = async () => {
      setLoading(true);
      setError(null);
      try {
        const skillList = await skillApi.listSkills();
        setSkills(skillList);
      } catch (err) {
        setError('加载技能列表失败');
        console.error('加载技能列表失败:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSkills();
  }, []);

  // 切换技能启用状态
  const handleToggle = useCallback((skillName: string) => {
    if (disabled) return;

    const newSkills = enabledSkills.includes(skillName)
      ? enabledSkills.filter(s => s !== skillName)
      : [...enabledSkills, skillName];
    onChange(newSkills);
  }, [enabledSkills, onChange, disabled]);

  // 渲染技能项
  const renderSkillItem = (skill: Skill) => {
    const isEnabled = enabledSkills.includes(skill.name);

    return (
      <div
        key={skill.name}
        className={`
          flex items-center justify-between p-3 rounded-lg border transition-colors
          ${disabled
            ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
            : isEnabled
              ? 'bg-paper-50 border-paper-200 cursor-pointer hover:bg-paper-100'
              : 'bg-white border-gray-200 cursor-pointer hover:bg-gray-50'
          }
        `}
        onClick={() => handleToggle(skill.name)}
      >
        <div className="flex items-center gap-3">
          {/* 复选框 */}
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={() => handleToggle(skill.name)}
            disabled={disabled}
            className="w-4 h-4 text-paper-700 rounded border-gray-300 focus:ring-paper-500"
            onClick={(e) => e.stopPropagation()}
          />

          {/* 技能信息 */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{skill.name}</span>
              {skill.is_system && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  系统
                </span>
              )}
            </div>
            {skill.description && (
              <p className="text-sm text-gray-600 mt-0.5 line-clamp-1">
                {skill.description}
              </p>
            )}
          </div>
        </div>

        {/* 预览按钮 */}
        {onPreviewSkill && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreviewSkill(skill.name);
            }}
            className="text-sm text-paper-700 hover:text-paper-800 px-2 py-1 rounded hover:bg-paper-100"
          >
            预览
          </button>
        )}
      </div>
    );
  };

  // 加载中状态
  if (loading) {
    return (
      <div className="py-4 text-center text-gray-600">
        加载技能列表...
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="py-4 text-center text-red-600">
        {error}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="ml-2 text-paper-700 hover:underline"
        >
          重试
        </button>
      </div>
    );
  }

  // 空状态
  if (skills.length === 0) {
    return (
      <div className="py-4 text-center text-gray-600">
        暂无可用技能
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-900">
          启用技能
        </label>
        <span className="text-sm text-gray-600">
          已选择 {enabledSkills.length} 个
        </span>
      </div>
      <div className="max-h-60 overflow-y-auto space-y-2">
        {skills.map(renderSkillItem)}
      </div>
    </div>
  );
};

export default SkillSelector;
